import React from 'react';
import styles from './CordexaCoreLoader.module.css';
import type { CordexaCoreVariant } from './types';

export type CordexaCoreLoaderProps = {
  variant?: CordexaCoreVariant;
  /** Calmer motion for in-message assistant thinking states. */
  calm?: boolean;
  className?: string;
};

type RingDef = { id: string; rx: number; ry: number; className: string };

type VariantConfig = {
  wrapClass: string;
  orbClass: string;
  haloClass: string;
  viewBox: string;
  center: number;
  orbitRadius: string;
  rings: RingDef[];
  particles: number;
  halo: boolean;
  particleClass: string;
};

const VARIANT_CONFIG: Record<CordexaCoreVariant, VariantConfig> = {
  inline: {
    wrapClass: styles.coreWrapInline,
    orbClass: styles.orbInline,
    haloClass: styles.haloInline,
    viewBox: '0 0 40 40',
    center: 20,
    orbitRadius: 'var(--loader-orbit-radius-inline)',
    rings: [{ id: 'ring-inline-1', rx: 17, ry: 7, className: 'ringInline1' }],
    particles: 1,
    halo: true,
    particleClass: styles.particleInline,
  },
  region: {
    wrapClass: styles.coreWrapRegion,
    orbClass: styles.orbRegion,
    haloClass: styles.haloRegion,
    viewBox: '0 0 60 60',
    center: 30,
    orbitRadius: 'var(--loader-orbit-radius-region)',
    rings: [
      { id: 'ring-region-1', rx: 26, ry: 10, className: 'ringRegion1' },
      { id: 'ring-region-2', rx: 23, ry: 12, className: 'ringRegion2' },
    ],
    particles: 2,
    halo: true,
    particleClass: styles.particleRegion,
  },
  app: {
    wrapClass: styles.coreWrapApp,
    orbClass: styles.orbApp,
    haloClass: styles.haloApp,
    viewBox: '0 0 88 88',
    center: 44,
    orbitRadius: 'var(--loader-orbit-radius-app)',
    rings: [
      { id: 'ring-app-1', rx: 36, ry: 13, className: 'ringApp1' },
      { id: 'ring-app-2', rx: 32, ry: 16, className: 'ringApp2' },
      { id: 'ring-app-3', rx: 38, ry: 11, className: 'ringApp3' },
    ],
    particles: 3,
    halo: true,
    particleClass: styles.particleApp,
  },
  hero: {
    wrapClass: styles.coreWrapHero,
    orbClass: styles.orbHero,
    haloClass: styles.haloHero,
    viewBox: '0 0 320 320',
    center: 160,
    orbitRadius: 'var(--loader-orbit-radius-hero)',
    rings: [
      { id: 'ring-hero-1', rx: 128, ry: 46, className: 'ringHero1' },
      { id: 'ring-hero-2', rx: 114, ry: 56, className: 'ringHero2' },
      { id: 'ring-hero-3', rx: 134, ry: 42, className: 'ringHero3' },
    ],
    particles: 3,
    halo: true,
    particleClass: styles.particleHero,
  },
};

const ringClassMap: Record<string, string> = {
  ringInline1: styles.ringInline1,
  ringRegion1: styles.ringRegion1,
  ringRegion2: styles.ringRegion2,
  ringApp1: styles.ringApp1,
  ringApp2: styles.ringApp2,
  ringApp3: styles.ringApp3,
  ringHero1: styles.ringHero1,
  ringHero2: styles.ringHero2,
  ringHero3: styles.ringHero3,
};

const PARTICLE_ORBITS = [
  styles.particleOrbit1,
  styles.particleOrbit2,
  styles.particleOrbit3,
] as const;

export function CordexaCoreLoader({
  variant = 'app',
  calm = false,
  className,
}: CordexaCoreLoaderProps) {
  const config = VARIANT_CONFIG[variant];

  return (
    <div
      className={[
        styles.coreWrap,
        config.wrapClass,
        variant === 'inline' ? styles.intensityInline : '',
        calm ? styles.intensityCalm : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ['--loader-orbit-radius' as string]: config.orbitRadius }}
      aria-hidden="true"
      data-testid="cordexa-core-loader"
      data-variant={variant}
    >
      {config.halo ? (
        <div className={[styles.halo, config.haloClass].join(' ')} />
      ) : null}

      {config.rings.length > 0 ? (
        <svg className={styles.rings} viewBox={config.viewBox} aria-hidden="true">
          {config.rings.map((ring) => (
            <ellipse
              key={ring.id}
              cx={config.center}
              cy={config.center}
              rx={ring.rx}
              ry={ring.ry}
              className={[styles.ring, ringClassMap[ring.className]].join(' ')}
            />
          ))}
        </svg>
      ) : null}

      {config.particles > 0
        ? PARTICLE_ORBITS.slice(0, config.particles).map((orbitClass, index) => (
            <span
              key={`particle-${index}`}
              className={[styles.particle, config.particleClass, orbitClass].join(' ')}
            />
          ))
        : null}

      <div className={[styles.orb, config.orbClass].join(' ')} />
    </div>
  );
}
